import { supabase } from '../lib/supabase';
import openai from '../lib/openai';

/**
 * Service for handling audition song recommendations using OpenAI
 */

// Rate limiting for OpenAI API calls
const rateLimiter = {
  calls: [],
  maxCallsPerMinute: 5, // Conservative limit for audition recommendations
  
  canMakeCall() {
    const now = Date.now();
    this.calls = this.calls?.filter(timestamp => now - timestamp < 60000);
    return this.calls?.length < this.maxCallsPerMinute;
  },
  
  recordCall() {
    this.calls?.push(Date.now());
  },
  
  getWaitTime() {
    if (this.calls?.length === 0) return 0;
    const oldestCall = Math.min(...this.calls);
    const waitTime = 60000 - (Date.now() - oldestCall);
    return Math.max(0, waitTime);
  }
};

/**
 * Search for roles in the database
 * @param {string} searchQuery - Search query for role name or musical
 * @param {Object} filters - Optional filters (voiceType, genre, etc.)
 * @returns {Promise<Array>} List of matching roles
 */
export async function searchRoles(searchQuery, filters = {}) {
  try {
    let query = supabase?.from('roles')?.select(`
        id,
        character_name,
        description,
        voice_type,
        vocal_range,
        age_range,
        role_type,
        musical_id,
        musicals (
          id,
          title,
          poster_url,
          genre,
          composer,
          year_premiered
        )
      `);

    // Apply search filter
    if (searchQuery && searchQuery?.trim()) {
      query = query?.or(`character_name.ilike.%${searchQuery}%,musicals.title.ilike.%${searchQuery}%`);
    }

    // Apply voice type filter
    if (filters?.voiceType) {
      query = query?.eq('voice_type', filters?.voiceType);
    }

    // Apply genre filter
    if (filters?.genre) {
      query = query?.eq('musicals.genre', filters?.genre);
    }

    // Order by relevance (character name match first, then musical title)
    query = query?.order('character_name', { ascending: true })?.limit(50);

    const { data, error } = await query;

    if (error) throw error;

    // Format the results
    return (data || [])?.map(role => ({
      roleId: role?.id,
      characterName: role?.character_name,
      description: role?.description,
      voiceType: role?.voice_type,
      vocalRange: role?.vocal_range,
      ageRange: role?.age_range,
      roleType: role?.role_type,
      musicalId: role?.musicals?.id,
      musicalTitle: role?.musicals?.title,
      musicalPoster: role?.musicals?.poster_url,
      musicalGenre: role?.musicals?.genre,
      composer: role?.musicals?.composer,
      yearPremiered: role?.musicals?.year_premiered
    }));
  } catch (error) {
    console.error('Error searching roles:', error);
    throw new Error(`Failed to search roles: ${error?.message}`);
  }
}

/**
 * Get detailed role information including songs
 * @param {string} roleId - Role ID
 * @returns {Promise<Object>} Detailed role information
 */
export async function getRoleDetails(roleId) {
  try {
    const { data, error } = await supabase?.from('roles')?.select(`
        *,
        musicals (
          id,
          title,
          poster_url,
          genre,
          composer,
          lyricist,
          year_premiered
        ),
        song_roles (
          id,
          is_solo,
          vocal_part,
          songs (
            id,
            title,
            act_number,
            key_signature
          )
        )
      `)?.eq('id', roleId)?.single();

    if (error) throw error;

    return {
      roleId: data?.id,
      characterName: data?.character_name,
      description: data?.description,
      voiceType: data?.voice_type,
      vocalRange: data?.vocal_range,
      ageRange: data?.age_range,
      roleType: data?.role_type,
      isPrincipal: data?.is_principal,
      musical: {
        id: data?.musicals?.id,
        title: data?.musicals?.title,
        posterUrl: data?.musicals?.poster_url,
        genre: data?.musicals?.genre,
        composer: data?.musicals?.composer,
        lyricist: data?.musicals?.lyricist,
        yearPremiered: data?.musicals?.year_premiered
      },
      songs: (data?.song_roles || [])?.map(sr => ({
        songId: sr?.songs?.id,
        title: sr?.songs?.title,
        actNumber: sr?.songs?.act_number,
        keySignature: sr?.songs?.key_signature,
        isSolo: sr?.is_solo,
        vocalPart: sr?.vocal_part
      }))
    };
  } catch (error) {
    console.error('Error fetching role details:', error);
    throw new Error(`Failed to fetch role details: ${error?.message}`);
  }
}

/**
 * Generate audition song recommendations using OpenAI
 * @param {string} userId - User ID
 * @param {string} roleId - Role ID
 * @param {Object} options - Additional options (auditionType, studentProfile, etc.)
 * @returns {Promise<Object>} Audition song recommendations
 */
export async function getAuditionSongRecommendations(userId, roleId, options = {}) {
  try {
    // Check for cached recommendations first
    const { data: cachedRec, error: cacheError } = await supabase?.from('audition_song_recommendations')?.select('*')?.eq('user_id', userId)?.eq('role_id', roleId)?.gt('expires_at', new Date()?.toISOString())?.single();

    if (!cacheError && cachedRec) {
      return {
        recommendations: cachedRec?.recommended_songs,
        reasoning: cachedRec?.reasoning,
        confidenceScore: parseFloat(cachedRec?.confidence_score || 0.8),
        cached: true
      };
    }

    // Get role details
    const roleDetails = await getRoleDetails(roleId);

    // Check rate limiting
    if (!rateLimiter?.canMakeCall()) {
      const waitTime = rateLimiter?.getWaitTime();
      throw new Error(`Rate limit reached. Please wait ${Math.ceil(waitTime / 1000)} seconds before requesting recommendations again.`);
    }

    // Check API key availability
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
      throw new Error('OpenAI API key not configured. Please contact support to enable AI recommendations.');
    }

    // Prepare the prompt for OpenAI
    const auditionType = options?.auditionType || 'general';
    const studentProfile = options?.studentProfile || {};

    const systemPrompt = `You are an expert musical theater audition coach with deep knowledge of repertoire selection. 
Your task is to recommend 3-5 audition songs for a student based on the role they are auditioning for.

Consider:
1. The role's voice type, vocal range, and character requirements 2. The musical's style and genre
3. The audition type (initial, callback, competition)
4. Song appropriateness and effectiveness for showcasing the student's abilities
5. Contrast and variety in song selection

Return a JSON object with this exact structure:
{
  "recommendations": [
    {
      "songTitle": "Song name",
      "musicalFrom": "Musical title",
      "reasoning": "Why this song is a good choice",
      "difficultLevel": "Beginner|Intermediate|Advanced",
      "vocalHighlights": "What this song showcases",
      "cautionNotes": "Optional: Any concerns or notes"
    }
  ],
  "overallStrategy": "Brief strategy for this audition approach",
  "confidenceScore": 0.85
}`;

    const userPrompt = `Student is auditioning for the role of "${roleDetails?.characterName}" in "${roleDetails?.musical?.title}".

Role Details:
- Voice Type: ${roleDetails?.voiceType}
- Vocal Range: ${roleDetails?.vocalRange || 'Not specified'}
- Age Range: ${roleDetails?.ageRange || 'Not specified'}
- Character Description: ${roleDetails?.description || 'Not provided'}
- Role Type: ${roleDetails?.roleType}
- Musical Genre: ${roleDetails?.musical?.genre}
- Composer: ${roleDetails?.musical?.composer}

Audition Type: ${auditionType}
${studentProfile?.voiceType ? `Student Voice Type: ${studentProfile?.voiceType}` : ''}
${studentProfile?.strengths ? `Student Strengths: ${studentProfile?.strengths}` : ''}

Recommend 3-5 audition songs that would be excellent choices for this audition. Focus on songs that showcase the student's abilities while being appropriate for the role.`;

    // Make OpenAI API call
    rateLimiter?.recordCall();
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      max_tokens: 2000,
      temperature: 0.7,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ]
    });

    // Parse the response
    const content = response?.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error('No response from OpenAI');
    }

    let parsedResponse;
    try {
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      parsedResponse = JSON.parse(jsonMatch ? jsonMatch?.[0] : content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', parseError);
      throw new Error('Failed to parse AI recommendations. Please try again.');
    }

    // Save recommendations to database
    const { error: saveError } = await supabase?.from('audition_song_recommendations')?.upsert({
        user_id: userId,
        role_id: roleId,
        recommended_songs: parsedResponse?.recommendations || [],
        reasoning: parsedResponse?.overallStrategy || '',
        confidence_score: parsedResponse?.confidenceScore || 0.8,
        audition_type: auditionType,
        student_vocal_profile: studentProfile,
        created_at: new Date()?.toISOString(),
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString() // 30 days
      }, {
        onConflict: 'user_id,role_id'
      });

    if (saveError) {
      console.error('Failed to save recommendations:', saveError);
      // Don't throw error - we still want to return the recommendations
    }

    return {
      recommendations: parsedResponse?.recommendations || [],
      reasoning: parsedResponse?.overallStrategy || '',
      confidenceScore: parsedResponse?.confidenceScore || 0.8,
      roleDetails,
      cached: false
    };
  } catch (error) {
    console.error('Error generating audition song recommendations:', error);
    
    // Provide user-friendly error messages
    if (error?.message?.includes('Rate limit')) {
      throw error;
    } else if (error?.message?.includes('API key')) {
      throw error;
    } else if (error?.message?.includes('quota')) {
      throw new Error('AI service temporarily unavailable due to quota limits. Please try again later or contact support.');
    } else {
      throw new Error(`Failed to generate recommendations: ${error?.message}`);
    }
  }
}

/**
 * Get user's previous recommendations
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of previous recommendations
 */
export async function getUserRecommendationHistory(userId) {
  try {
    const { data, error } = await supabase?.from('audition_song_recommendations')?.select(`
        *,
        roles (
          character_name,
          musicals (
            title,
            poster_url
          )
        )
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);

    if (error) throw error;

    return (data || [])?.map(rec => ({
      id: rec?.id,
      roleId: rec?.role_id,
      characterName: rec?.roles?.character_name,
      musicalTitle: rec?.roles?.musicals?.title,
      musicalPoster: rec?.roles?.musicals?.poster_url,
      recommendations: rec?.recommended_songs,
      reasoning: rec?.reasoning,
      confidenceScore: parseFloat(rec?.confidence_score || 0.8),
      auditionType: rec?.audition_type,
      createdAt: rec?.created_at,
      expiresAt: rec?.expires_at
    }));
  } catch (error) {
    console.error('Error fetching recommendation history:', error);
    throw new Error(`Failed to fetch recommendation history: ${error?.message}`);
  }
}

/**
 * Delete a recommendation
 * @param {string} userId - User ID
 * @param {string} recommendationId - Recommendation ID
 * @returns {Promise<void>}
 */
export async function deleteRecommendation(userId, recommendationId) {
  try {
    const { error } = await supabase?.from('audition_song_recommendations')?.delete()?.eq('id', recommendationId)?.eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting recommendation:', error);
    throw new Error(`Failed to delete recommendation: ${error?.message}`);
  }
}

/**
 * Check if OpenAI service is available
 * @returns {Promise<Object>} Service availability status
 */
export async function checkAIServiceAvailability() {
  try {
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
      return {
        available: false,
        reason: 'no_api_key',
        message: 'OpenAI API key not configured'
      };
    }

    if (!rateLimiter?.canMakeCall()) {
      const waitTime = rateLimiter?.getWaitTime();
      return {
        available: false,
        reason: 'rate_limited',
        message: `Rate limit reached. Please wait ${Math.ceil(waitTime / 1000)} seconds.`,
        waitTime: waitTime
      };
    }

    return {
      available: true,
      reason: 'operational',
      message: 'AI service is ready'
    };
  } catch (error) {
    return {
      available: false,
      reason: 'error',
      message: error?.message
    };
  }
}

export default {
  searchRoles,
  getRoleDetails,
  getAuditionSongRecommendations,
  getUserRecommendationHistory,
  deleteRecommendation,
  checkAIServiceAvailability,
  searchSongsForAudition,
  rateLimiter
};

/**
 * Search for songs by title or musical name for audition assignment
 * @param {string} query - Search query
 * @returns {Promise<Object>} { data, error }
 */
export async function searchSongsForAudition(query) {
  try {
    const { data, error } = await supabase
      ?.from('songs')
      ?.select(`
        id,
        title,
        musical_id,
        musicals (
          id,
          title,
          composer,
          genre,
          poster_url
        )
      `)
      ?.ilike('title', `%${query}%`)
      ?.order('title', { ascending: true })
      ?.limit(30);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error searching songs for audition:', error);
    return { data: [], error };
  }
}

/**
 * Get all audition songs with their related song and musical data
 * @returns {Promise<Object>} Audition songs data with error handling
 */
export async function getAuditionSongs() {
  try {
    const { data, error } = await supabase
      ?.from('audition_songs')
      ?.select(`
        id,
        song_id,
        voice_type,
        category,
        recommended_age_range,
        difficulty_notes,
        is_active,
        created_at,
        updated_at,
        songs (
          id,
          title,
          musical_id,
          musicals (
            id,
            title,
            poster_url
          )
        )
      `)
      ?.eq('is_active', true)
      ?.order('voice_type', { ascending: true })
      ?.order('category', { ascending: true });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching audition songs:', error);
    return { data: null, error: error?.message };
  }
}

/**
 * Add a new audition song assignment
 * @param {Object} auditionSongData - Audition song data
 * @returns {Promise<Object>} Created audition song with error handling
 */
export async function addAuditionSong(auditionSongData) {
  try {
    const { data, error } = await supabase
      ?.from('audition_songs')
      ?.insert([{
        song_id: auditionSongData?.songId,
        voice_type: auditionSongData?.voiceType,
        category: auditionSongData?.category,
        recommended_age_range: auditionSongData?.recommendedAgeRange,
        difficulty_notes: auditionSongData?.difficultyNotes,
        is_active: true
      }])
      ?.select()
      ?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error adding audition song:', error);
    return { data: null, error: error?.message };
  }
}

/**
 * Remove an audition song assignment
 * @param {string} auditionSongId - Audition song ID to remove
 * @returns {Promise<Object>} Result with error handling
 */
export async function removeAuditionSong(auditionSongId) {
  try {
    const { error } = await supabase
      ?.from('audition_songs')
      ?.delete()
      ?.eq('id', auditionSongId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error removing audition song:', error);
    return { error: error?.message };
  }
}

/**
 * Update audition song details
 * @param {string} auditionSongId - Audition song ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated audition song with error handling
 */
export async function updateAuditionSong(auditionSongId, updates) {
  try {
    const updateData = {};
    
    if (updates?.recommendedAgeRange !== undefined) {
      updateData.recommended_age_range = updates?.recommendedAgeRange;
    }
    if (updates?.difficultyNotes !== undefined) {
      updateData.difficulty_notes = updates?.difficultyNotes;
    }
    if (updates?.isActive !== undefined) {
      updateData.is_active = updates?.isActive;
    }

    const { data, error } = await supabase
      ?.from('audition_songs')
      ?.update(updateData)
      ?.eq('id', auditionSongId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating audition song:', error);
    return { data: null, error: error?.message };
  }
}